/**
 * Phase 134 — StreamX Stream-End Discovery Widget
 * --------------------------------------------------------------------
 * Rendered below the "Stream ended" trophy on /livex/watch/:id when the
 * current stream's status is "ended". Pulls 3 currently-live streams via
 * GET /api/livex/streams/live?exclude={current}&sport={sport} so the
 * recommended carousel prioritises same-sport matches.
 *
 * Network-effect infrastructure: keeps a viewer in-platform instead of
 * bouncing to YouTube after a match ends.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Radio, Eye, ArrowRight, Trophy, Sparkles } from 'lucide-react';
import '@/styles/livex.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LivexStreamEndDiscovery({ currentStreamId, sport }) {
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const params = new URLSearchParams();
    params.set('limit', '6');
    if (currentStreamId) params.set('exclude', currentStreamId);
    if (sport) params.set('sport', sport);
    axios
      .get(`${API}/livex/streams/live?${params.toString()}`)
      .then((r) => {
        if (!active) return;
        setStreams((r.data?.entries || []).slice(0, 3));
      })
      .catch(() => active && setStreams([]))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [currentStreamId, sport]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="livex-stream-end-loading">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="bg-[#07111F] border-2 border-[#22314F] rounded-xl overflow-hidden">
            <div className="aspect-video bg-[#0c1a2e] livex-shimmer" />
            <div className="p-3 space-y-2">
              <div className="h-4 w-3/4 livex-shimmer rounded" />
              <div className="h-3 w-1/2 livex-shimmer rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (streams.length === 0) {
    return (
      <div className="p-6 bg-[#07111F] border-2 border-[#22314F] rounded-xl text-center" data-testid="livex-stream-end-empty">
        <Sparkles className="h-8 w-8 text-[#FF5A1F] mx-auto mb-2" />
        <div className="text-base font-black text-white mb-1">No other live matches right now</div>
        <p className="livex-body-text mb-4" style={{ fontSize: '13px' }}>Be the first to know when academies go live — drop in any time.</p>
        <Link
          to="/streamx"
          data-testid="livex-stream-end-empty-cta"
          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold text-white border-2 border-[#FF5A1F] rounded-lg transition-all hover:-translate-y-0.5"
          style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}
        >
          Explore StreamX <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="livex-stream-end-discovery">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Trophy className="h-4 w-4 text-amber-400" />
          <div>
            <h2 className="text-base sm:text-lg font-black text-white tracking-tight leading-tight">Watch another live match</h2>
            <p className="text-[11px] font-bold text-[#94A3B8] uppercase tracking-[0.12em]">Happening right now on StreamX</p>
          </div>
        </div>
        <Link
          to="/streamx/live"
          data-testid="livex-stream-end-see-all"
          className="text-[12px] font-bold text-[#FF5A1F] hover:text-orange-400 inline-flex items-center gap-1"
        >
          See all <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {streams.map((s) => (
          <Link
            key={s.id}
            to={`/livex/watch/${s.id}`}
            data-testid={`livex-stream-end-card-${s.id}`}
            className="livex-card-lift block bg-[#07111F] border-2 border-[#22314F] hover:border-[#FF5A1F] rounded-xl overflow-hidden"
          >
            <div className="aspect-video bg-gradient-to-br from-[#0c1a2e] to-[#050a14] relative">
              <div className="absolute inset-0 grid place-items-center">
                <div className="h-12 w-12 rounded-2xl grid place-items-center" style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}>
                  <Radio className="h-5 w-5 text-white" />
                </div>
              </div>
              <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-1.5 py-0.5 bg-rose-600 text-white text-[10px] font-black uppercase tracking-wider rounded">
                <span className="h-1.5 w-1.5 rounded-full bg-white livex-live-dot" /> LIVE
              </span>
              <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-1.5 py-0.5 bg-black/70 backdrop-blur text-white text-[10px] font-bold rounded">
                <Eye className="h-2.5 w-2.5" /> {s.viewer_count || 0}
              </span>
            </div>
            <div className="p-3">
              <h3 className="text-sm font-black text-white truncate">{s.title}</h3>
              <div className="text-[11px] font-bold text-[#94A3B8] truncate mt-0.5">
                {s.sport && <span className="capitalize">{s.sport}</span>}
                {s.academy_name && (s.sport ? <> · {s.academy_name}</> : s.academy_name)}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
